import { useState } from 'react';
import { requireSatellite } from './config';
import { SatelliteLayout } from './layout';
import { trackSatelliteAction } from './analytics';
import { formatYmd, todayYmd } from './dates';

const CONFIG = requireSatellite('visitationtracker.com');

type Outcome = 'on-time' | 'late' | 'no-show';

type LogRow = {
  id: number;
  date: string;
  planned: string;
  arrived: string;
  outcome: Outcome;
  notes: string;
};

const OUTCOME_LABEL: Record<Outcome, string> = {
  'on-time': 'On time',
  late: 'Late',
  'no-show': 'No-show',
};

let nextId = 2;

export function VisitationTrackerPage() {
  const [rows, setRows] = useState<LogRow[]>([
    {
      id: 1,
      date: formatYmd(todayYmd()),
      planned: '18:00',
      arrived: '',
      outcome: 'on-time',
      notes: '',
    },
  ]);
  const [missedDate, setMissedDate] = useState(formatYmd(todayYmd()));
  const [whoExpected, setWhoExpected] = useState('the other parent');
  const [whatHappened, setWhatHappened] = useState('did not arrive for the scheduled exchange');
  const [copied, setCopied] = useState(false);

  const missedNote = `Missed exchange on ${missedDate}. ${whoExpected} ${whatHappened}. I was present at the agreed location and time. This note records what I observed; it is not legal advice.`;

  function addRow() {
    setRows((current) => [
      ...current,
      {
        id: nextId,
        date: formatYmd(todayYmd()),
        planned: '18:00',
        arrived: '',
        outcome: 'on-time',
        notes: '',
      },
    ]);
    nextId += 1;
  }

  function updateRow(id: number, patch: Partial<LogRow>) {
    setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }

  async function copyMissed() {
    try {
      await navigator.clipboard.writeText(missedNote);
      setCopied(true);
      trackSatelliteAction('Satellite Letter Copied', CONFIG, { template: 'missed-exchange' });
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <SatelliteLayout config={CONFIG}>
      <p className='text-sm text-gray-500'>
        Factual notes only — date, time, who arrived. This is not a court form or legal advice.
      </p>

      <div className='mt-4 overflow-x-auto'>
        <table className='w-full min-w-[40rem] text-left text-sm'>
          <thead>
            <tr className='border-b text-xs uppercase text-gray-500'>
              <th className='py-2 pr-2'>Date</th>
              <th className='py-2 pr-2'>Planned</th>
              <th className='py-2 pr-2'>Arrived</th>
              <th className='py-2 pr-2'>Outcome</th>
              <th className='py-2'>Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className='border-b align-top'>
                <td className='py-2 pr-2'>
                  <input
                    type='date'
                    className='w-full rounded border border-gray-300 px-2 py-1'
                    value={row.date}
                    onChange={(event) => updateRow(row.id, { date: event.target.value })}
                  />
                </td>
                <td className='py-2 pr-2'>
                  <input
                    type='time'
                    className='w-full rounded border border-gray-300 px-2 py-1'
                    value={row.planned}
                    onChange={(event) => updateRow(row.id, { planned: event.target.value })}
                  />
                </td>
                <td className='py-2 pr-2'>
                  <input
                    type='time'
                    className='w-full rounded border border-gray-300 px-2 py-1'
                    value={row.arrived}
                    onChange={(event) => updateRow(row.id, { arrived: event.target.value })}
                  />
                </td>
                <td className='py-2 pr-2'>
                  <select
                    className='w-full rounded border border-gray-300 px-2 py-1'
                    value={row.outcome}
                    onChange={(event) =>
                      updateRow(row.id, { outcome: event.target.value as Outcome })
                    }
                  >
                    {Object.entries(OUTCOME_LABEL).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className='py-2'>
                  <input
                    className='w-full rounded border border-gray-300 px-2 py-1'
                    value={row.notes}
                    onChange={(event) => updateRow(row.id, { notes: event.target.value })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className='mt-4 flex flex-wrap gap-3 print:hidden'>
        <button
          type='button'
          className='rounded-xl border border-gray-300 px-4 py-2 font-semibold hover:bg-gray-50'
          onClick={addRow}
        >
          Add row
        </button>
        <button
          type='button'
          className='rounded-xl bg-primary px-4 py-2 font-semibold text-white hover:bg-primary-hover'
          onClick={() => window.print()}
        >
          Print log
        </button>
      </div>

      <section className='mt-10 print:hidden'>
        <h2 className='text-lg font-semibold'>Missed-exchange note</h2>
        <div className='mt-3 grid gap-3 sm:grid-cols-2'>
          <label className='text-sm'>
            <span className='mb-1 block font-medium'>Date</span>
            <input
              type='date'
              className='w-full rounded-lg border border-gray-300 px-3 py-2'
              value={missedDate}
              onChange={(event) => setMissedDate(event.target.value)}
            />
          </label>
          <label className='text-sm'>
            <span className='mb-1 block font-medium'>Who was expected</span>
            <input
              className='w-full rounded-lg border border-gray-300 px-3 py-2'
              value={whoExpected}
              onChange={(event) => setWhoExpected(event.target.value)}
            />
          </label>
          <label className='text-sm sm:col-span-2'>
            <span className='mb-1 block font-medium'>What happened</span>
            <input
              className='w-full rounded-lg border border-gray-300 px-3 py-2'
              value={whatHappened}
              onChange={(event) => setWhatHappened(event.target.value)}
            />
          </label>
        </div>
        <pre className='mt-3 whitespace-pre-wrap rounded-2xl bg-gray-50 p-4 text-sm text-gray-800'>
          {missedNote}
        </pre>
        <button
          type='button'
          className='mt-3 rounded-xl border border-gray-300 px-4 py-2 font-semibold hover:bg-gray-50'
          onClick={() => void copyMissed()}
        >
          {copied ? 'Copied' : 'Copy note'}
        </button>
      </section>
    </SatelliteLayout>
  );
}
